import { Component, OnInit, OnDestroy, ViewChild, ElementRef, inject, ChangeDetectionStrategy, signal } from '@angular/core';

import { FormsModule } from '@angular/forms';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogModule,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { SnapshotService } from '../../shared/services/snapshot/snapshot.service';
import { ToastrService } from 'ngx-toastr';
import { AutoUnsubscribe } from '../../shared/decorators/auto-unsubscribe.decorator';
import { Subscription } from 'rxjs';
import { CacheService } from '../../shared/services/cache/cache.service';
import { NotebookVariablesService } from '../../shared/services/notebook-variables/notebook-variables.service';
import { SnapshotCreate } from '../../shared/interfaces/snapshot.interface';
import { Cache } from '../../shared/interfaces/cache.interface';

export interface CreateSnapshotModalData {
  projectId: number;
  versionId: number;
  workflowId?: number;
}

export interface CreateSnapshotModalResult {
  action: 'create' | 'cancel';
  snapshotName?: string;
}

@AutoUnsubscribe()
@Component({
  selector: 'app-create-snapshot-modal',
  standalone: true,
  imports: [FormsModule, MatDialogModule, MatButtonModule, MatIconModule],
  templateUrl: './create-snapshot-modal.component.html',
  styleUrls: ['./create-snapshot-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateSnapshotModalComponent implements OnInit, OnDestroy {
  private dialogRef = inject<MatDialogRef<CreateSnapshotModalComponent>>(MatDialogRef);
  data = inject<CreateSnapshotModalData>(MAT_DIALOG_DATA);
  private readonly snapshotService = inject(SnapshotService);
  private toastrService = inject(ToastrService);
  private cacheService = inject(CacheService);
  private notebookVariablesService = inject(NotebookVariablesService);

  @ViewChild('nameInput') nameInput?: ElementRef<HTMLInputElement>;

  snapshotName: string = '';
  description: string = '';
  isCreating = signal(false);

  private subscriptions: Subscription[] = [];

  ngOnInit() {
    // Focus the name input after the modal opens
    setTimeout(() => {
      this.nameInput?.nativeElement.focus();
    }, 100);
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  onNameInput() {
    // Remove any leading/trailing spaces and limit length
    this.snapshotName = this.snapshotName.trim();
  }

  onCancel() {
    this.dialogRef.close({
      action: 'cancel',
    } as CreateSnapshotModalResult);
  }

  onCreateSnapshot() {
    if (!this.snapshotName.trim()) {
      this.toastrService.error('Please enter a snapshot name');
      return;
    }

    if (this.snapshotName.length > 100) {
      this.toastrService.error(
        'Snapshot name is too long (max 100 characters)',
      );
      return;
    }

    this.isCreating.set(true);
    this.createSnapshot(this.snapshotName.trim());
  }

  private createSnapshot(snapshotName: string) {
    this.cacheService.get().subscribe({
      next: (res: Cache | null) => {
        if (!res) {
          this.toastrService.error(
            'Failed to get cache data for snapshot creation',
          );
          this.isCreating.set(false);
          return;
        }

        const data = {
          name: snapshotName,
          cacheId: res.cacheId,
          content:
            Array.isArray(this.notebookVariablesService.variableList) &&
            this.notebookVariablesService.variableList.length > 0
              ? JSON.stringify(this.notebookVariablesService.variableList)
              : '',
        } as SnapshotCreate;

        this.snapshotService.createSnapshot(data).subscribe({
          next: () => {
            this.toastrService.success('Snapshot created successfully');
            this.dialogRef.close({
              action: 'create',
              snapshotName: snapshotName,
            } as CreateSnapshotModalResult);
          },
          error: (error) => {
            console.error('Error creating snapshot:', error);
            this.toastrService.error('Failed to create snapshot');
            this.isCreating.set(false);
          },
        });
      },
      error: (error) => {
        console.error('Error getting cache:', error);
        this.toastrService.error(
          'Failed to get cache data for snapshot creation',
        );
        this.isCreating.set(false);
      },
    });
  }

  get isValid(): boolean {
    return (
      this.snapshotName.trim().length > 0 && this.snapshotName.length <= 100
    );
  }

  get remainingChars(): number {
    return 100 - this.snapshotName.length;
  }
}
